/**
 * @component TaskWidget
 * @description Aufgaben-Widget fuer das Dashboard. Zeigt offene Tasks mit
 * Prioritaet (kritisch/wichtig/info), Deadline und Kategorie.
 * Erlaubt Erledigen und Ignorieren direkt aus der Uebersicht.
 *
 * @usage Eingebunden auf der Home-Seite als Dashboard-Widget
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button } from './ui';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API_URL || '';

const prioStyles = {
  kritisch: { dot: 'bg-red-500', text: 'text-red-500' },
  wichtig:  { dot: 'bg-amber-500', text: 'text-amber-500' },
  info:     { dot: 'bg-[var(--text-tertiary)]', text: 'text-[var(--text-tertiary)]' },
};

function formatDeadline(d, t) {
  if (!d) return null;
  const date = new Date(d);
  const now = new Date();
  const diff = Math.floor((date - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return t('tasks.overdue', { days: Math.abs(diff) });
  if (diff === 0) return t('tasks.dueToday');
  if (diff === 1) return t('tasks.dueTomorrow');
  return date.toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' });
}

function TaskItem({ task, onErledigt, onIgnorieren, onNavigate, t }) {
  const prio = prioStyles[task.prioritaet] || prioStyles.info;
  return (
    <div className="group flex items-start gap-3 py-2.5 px-3 hover:bg-[var(--bg-card-hover)] rounded-lg transition-colors">
      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${prio.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-[var(--text-primary)] leading-tight">
          {task.titel}
        </div>
        <div className="flex items-center gap-2 mt-1">
          {task.deadline && (
            <span className="text-[11px] text-[var(--text-tertiary)]">
              {formatDeadline(task.deadline, t)}
            </span>
          )}
          <Badge variant="default">{task.kategorie}</Badge>
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
        {task.link && (
          <button onClick={() => onNavigate(task.link)} className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" title={t('common.open')}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </button>
        )}
        <button onClick={() => onErledigt(task.id)} className="p-1 rounded text-emerald-500 hover:bg-emerald-500/10 transition-colors" title="Erledigt">
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
        </button>
        <button onClick={() => onIgnorieren(task.id)} className="p-1 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors" title={t('tasks.ignoreDays')}>
          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" /></svg>
        </button>
      </div>
    </div>
  );
}

export default function TaskWidget({ className = '' }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [tasks, setTasks] = useState([]);
  const [counts, setCounts] = useState({ total: 0, kritisch: 0, wichtig: 0, info: 0 });
  const [filter, setFilter] = useState('alle');
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    try {
      const [tasksRes, countRes] = await Promise.all([
        fetch(`${API}/api/tasks`, { credentials: 'include' }).then(r => r.ok ? r.json() : { tasks: [] }),
        fetch(`${API}/api/tasks/count`, { credentials: 'include' }).then(r => r.ok ? r.json() : { total: 0, kritisch: 0, wichtig: 0, info: 0 }),
      ]);
      setTasks(tasksRes.tasks || []);
      setCounts(countRes);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleErledigt = async (id) => {
    await fetch(`${API}/api/tasks/${id}/erledigt`, { method: 'POST', credentials: 'include' });
    fetchTasks();
  };

  const handleIgnorieren = async (id) => {
    await fetch(`${API}/api/tasks/${id}/ignorieren`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tage: 30 }),
    });
    fetchTasks();
  };

  const filtered = filter === 'alle' ? tasks : tasks.filter(t => t.prioritaet === filter);
  const shown = filtered.slice(0, 5);

  if (loading) {
    return (
      <div className={`bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card px-4 py-3.5 ${className}`}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-[var(--bg-inset)] rounded w-1/3" />
          <div className="h-3 bg-[var(--bg-inset)] rounded w-2/3" />
          <div className="h-3 bg-[var(--bg-inset)] rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (counts.total === 0) {
    return (
      <div className={`bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card px-4 py-3.5 ${className}`}>
        <div className="text-sm font-medium text-[var(--text-primary)] mb-3">Meine Aufgaben</div>
        <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">Keine offenen Aufgaben</div>
      </div>
    );
  }

  return (
    <div className={`bg-[var(--bg-card)] border border-[var(--border-card)] rounded-card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
        <div className="text-sm font-medium text-[var(--text-primary)]">Meine Aufgaben</div>
        {counts.kritisch > 0 && (
          <span className="text-[11px] font-medium text-red-500">{counts.kritisch} kritisch</span>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-1 px-4 pb-2">
        {[
          { key: 'alle', label: 'Alle', count: counts.total },
          { key: 'kritisch', label: null, count: counts.kritisch, dot: 'bg-red-500' },
          { key: 'wichtig', label: null, count: counts.wichtig, dot: 'bg-amber-500' },
          { key: 'info', label: null, count: counts.info, dot: 'bg-[var(--text-tertiary)]' },
        ].filter(f => f.count > 0 || f.key === 'alle').map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
              filter === f.key
                ? 'bg-[var(--bg-active)] text-[var(--text-primary)]'
                : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
            }`}
          >
            {f.dot && <span className={`w-1.5 h-1.5 rounded-full ${f.dot}`} />}
            {f.label || ''}{f.count > 0 ? ` ${f.count}` : ''}
          </button>
        ))}
      </div>

      {/* Task List */}
      <div className="px-1 pb-1">
        {shown.map(task => (
          <TaskItem
            key={task.id}
            task={task}
            onErledigt={handleErledigt}
            onIgnorieren={handleIgnorieren}
            onNavigate={(link) => navigate(link)}
            t={t}
          />
        ))}
      </div>

      {/* Footer */}
      {filtered.length > 5 && (
        <div className="px-4 pb-3.5 pt-1">
          <button
            onClick={() => navigate('/aufgaben')}
            className="text-[11px] font-medium text-primary-500 hover:text-primary-400 transition-colors"
          >
            Alle anzeigen ({filtered.length}) →
          </button>
        </div>
      )}
    </div>
  );
}
